import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import type { SelectOption } from '@/components/molecules'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { usePetVets } from '@/shared/hooks/queries/use-pet-vets'
import { YES_NO_UNKNOWN } from '@/shared/types/global.types'
import type { Pet } from '@/shared/types/global.types'
import type { PetFormValues } from '../page.onboarding.pet-form.hook'

interface UsePetFormStepTwoProps {
  pet?: Pet
}

export function usePetFormStepTwo({ pet }: UsePetFormStepTwoProps) {
  const { t } = useTranslation()
  const { setValue } = useFormContext<PetFormValues>()

  const [vetSearch, setVetSearch] = useState('')
  const debouncedVetSearch = useDebouncedValue(vetSearch)
  const { data: vets = [], isFetching: isVetsFetching } =
    usePetVets(debouncedVetSearch)
  const [selectedVet, setSelectedVet] = useState<SelectOption | undefined>(
    undefined,
  )

  const isVetSearchLoading = isVetsFetching || debouncedVetSearch !== vetSearch

  useEffect(() => {
    if (selectedVet || !pet?.primaryVetId || !pet?.primaryVetName) return
    setSelectedVet({ value: pet.primaryVetId, label: pet.primaryVetName })
  }, [pet, selectedVet])

  const vetOptions = useMemo<SelectOption[]>(() => {
    const opts = vets.map((v) => ({
      value: String(v.vetId),
      label: v.vetName,
    }))
    if (selectedVet && !opts.some((o) => o.value === selectedVet.value)) {
      return [selectedVet, ...opts]
    }
    return opts
  }, [vets, selectedVet])

  function handleSelectVet(value: string) {
    setValue('primaryVetId', value, { shouldDirty: true })
    const found = vetOptions.find((o) => o.value === value)
    if (found) {
      setSelectedVet(found)
      setValue('primaryVetName', found.label)
    }
  }

  const yesNoOptions = [
    { value: YES_NO_UNKNOWN.YES, label: t('onboarding.pet.form.yesNo.yes') },
    { value: YES_NO_UNKNOWN.NO, label: t('onboarding.pet.form.yesNo.no') },
    {
      value: YES_NO_UNKNOWN.I_DONT_KNOW,
      label: t('onboarding.pet.form.yesNo.unknown'),
    },
  ]

  return {
    vetOptions,
    setVetSearch,
    handleSelectVet,
    yesNoOptions,
    isVetSearchLoading,
  }
}
